import React, { useState, useRef, useCallback } from "react";
import { LoadScript, GoogleMap, Polygon, Marker, Autocomplete } from "@react-google-maps/api";
import Spinner from "../shared/Spinner";
import { ClearOutlined, DoneOutlined, RectangleOutlined } from "@mui/icons-material";

const libraries = ["places"];
const polygonOptions = {
  fillColor: "#fff",
  fillOpacity: 0.5,
  strokeColor: "#fff",
  strokeOpacity: 1,
  strokeWeight: 2,
  zIndex: 1
}

export default function Map(props){
  const [spinner, setSpinner] = useState(true);
  const [autocomplete, setAutocomplete ] = useState(null);
  const [mapLoad, setMapLoad ] = useState(false);

  // Define refs for Polygon instance and listeners
  const polygonRef = useRef(null);
  const listenersRef = useRef([]);

  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingPath, setDrawingPath] = useState([]);

  const onPolygonComplete = (polygon, destroy = false) => {
    let coords = getPaths(polygon);
    props.setPolygon(coords);

    // // Destroys the polygon that has been drawn by the manager.
    if (destroy) {
      polygon.setMap(null);
    }
  };

  const getPaths = (polygon) => {
    var polygonBounds = polygon.getPath();
    var bounds = [];
    for (var i = 0; i < polygonBounds.length; i++) {
      var point = {
        lat: polygonBounds.getAt(i).lat(),
        lng: polygonBounds.getAt(i).lng()
      };
      bounds.push(point);
    }
    return bounds;
  }

  // Call setPath with new edited path
  const onEdit = useCallback(() => {
    if (polygonRef.current) {
      const nextPath = polygonRef.current
        .getPath()
        .getArray()
        .map(latLng => {
          return { lat: latLng.lat(), lng: latLng.lng() };
        });
      props.setPolygon(nextPath);
    }
  }, [props.setPolygon]);

  // Bind refs to current Polygon and listeners
  const onLoad = useCallback(
    polygon => {
      polygonRef.current = polygon;
      const path = polygon.getPath();
      listenersRef.current.push(
        path.addListener("set_at", onEdit),
        path.addListener("insert_at", onEdit),
        path.addListener("remove_at", onEdit)
      );
    },
    [onEdit]
  );

  // Clean up refs
  const onUnmount = useCallback(() => {
    listenersRef.current.forEach(lis => lis.remove());
    polygonRef.current = null;
  }, []);

  const [markers , setMarkers] = useState([]);
  const mapClick= (e)=>{
    // setMarkers(markers => [...markers, {lat: e.latLng.lat(), lng: e.latLng.lng()}])
    const point = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng()
    };

    if (isDrawing) {
      setDrawingPath(prev => [...prev, point]);
    } else {
      setMarkers(prev => [...prev, point]);
    }
  }

  const finishPolygon = () => {
    if (drawingPath.length < 3) {
      alert("Polygon requires at least 3 points");
      return;
    }

    props.setPolygon(drawingPath);

    setDrawingPath([]);
    setIsDrawing(false);
  };

  const cancelDrawing = () => {
    setDrawingPath([]);
    setIsDrawing(false);
  };

  return (
    <div className="App" style={{height: props.height}}>
      <LoadScript
        id="script-loader"
        googleMapsApiKey="AIzaSyDcPvfjCIqeUpGe8_IaI_-5rIZX9qtCUnI"
        libraries={libraries}
        language="en"
        region="us"
        onLoad={()=>{setMapLoad(true);setSpinner(false)}}
      >
        <GoogleMap
          mapContainerClassName="App-map"
          center={props.center}
          zoom={props.zoom}
          version="weekly"
          on
          options={{
            mapTypeId: "satellite",
            draggableCursor: isDrawing ? "crosshair" : "grab"
          }}
          onClick={mapClick}
        >
          {props.editable && (
            <div
              style={{
                position: "absolute",
                top: "10px",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 999
              }}
            >
              <button
                type='button'
                onClick={() => {
                  setDrawingPath([]);
                  setIsDrawing(true);
                }}
              >
                <RectangleOutlined/>
              </button>

              <button
                type='button'
                disabled={drawingPath.length < 3}
                onClick={finishPolygon}
              >
                <DoneOutlined/>
              </button>

              <button 
                type='button'
                onClick={cancelDrawing}>
                <ClearOutlined/>
              </button>
            </div>
          )}
          {props.editable && 
          markers.map(x => {
            return(
              <Marker 
                position={x}
              />
            )
          })}
          {props.editable && <Autocomplete
            onLoad={(e)=>setAutocomplete(e)}
            onPlaceChanged={()=>{
              props.setCenter({
                lat: autocomplete.getPlace().geometry.location.lat(),
                lng: autocomplete.getPlace().geometry.location.lng()
              })
              props.setZoom(18)
            }}
          >
            <input
              type="text"
              placeholder={props.literals.search+" "+props.literals.address+"..."}
              style={{
                boxSizing: `border-box`,
                border: `1px solid transparent`,
                width: `80%`,
                height: `40px`,
                padding: `0 12px`,
                borderRadius: `3px`,
                boxShadow: `0 2px 6px rgba(0, 0, 0, 0.3)`,
                fontSize: `12px`,
                outline: `none`,
                textOverflow: `ellipses`,
                position: "absolute",
                left: "10%",
                top: '12%',
              }}
            />
          </Autocomplete>}
          {isDrawing && drawingPath.length > 0 && (
            <Polygon
              path={drawingPath}
              options={{
                strokeColor: "#808080",
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: "#808080",
                fillOpacity: 0.2
              }}
            />
          )}
          {props.editable && <Polygon
            editable
            path={props.polygon}
            options={polygonOptions}
            // Event used when manipulating and adding points
            onMouseUp={onEdit}
            onLoad={onLoad}
            onUnmount={onUnmount}
          />}
          {!props.editable &&<Polygon
            editable
            path={props.polygon}
            options={polygonOptions}
          />}
          {/* {props.editable && mapLoad && <DrawingManager
            options={{
              drawingControl: true,
              drawingControlOptions: {
                position: window.google.maps.ControlPosition.TOP_CENTER,
                drawingModes: [window.google.maps.drawing.OverlayType.POLYGON]
              },
            }}
            onPolygonComplete={(polygon) => onPolygonComplete(polygon, true)}
          />} */}
        </GoogleMap>
      </LoadScript>
      <Spinner
        spinner = {spinner}
      />
    </div>
  );
}
